/**
 * Unified row catalog for the onboarding "Custom CV" sheet: the 5 curated
 * presets merged with the REAL CV skill catalog, in one list both platform
 * sheets render.
 *
 * Merge rules:
 * - Three presets are shadows of catalog skills (they predate the sheet being
 *   able to apply real skills). When the catalog row is available, the real
 *   skill REPLACES its shadow preset in place, so the familiar list order is
 *   kept but the pick now applies the full SKILL.md server-side.
 * - Presets without a catalog twin (HR, career switch) stay as presets.
 * - Every remaining catalog CV skill is appended after the curated five, in
 *   the server's order (country-flag skills first).
 * - If the catalog hasn't loaded (or failed), the list is exactly the 5
 *   presets: today's behaviour, nothing lost.
 *
 * A skill pick is persisted as `styleSegmentId: "skill:<skillId>"`; the server
 * (OnboardingSkillStyleService) resolves that prefix to the actual SKILL.md.
 */
import type { SkillSummary } from '@tabbio/types';
import {
  skillAccentToCvColor,
  skillLayoutSchema,
  skillTemplateToCvStyle,
} from '@tabbio/utils';
import {
  CV_PRESETS,
  DEFAULT_CV_PRESET_LAYOUT,
  type CvPreset,
  type CvPresetLayoutConfig,
} from './cv-presets';

export const SKILL_STYLE_PREFIX = 'skill:';

export function skillStyleId(skillId: string): string {
  return `${SKILL_STYLE_PREFIX}${skillId}`;
}

export function parseSkillStyleId(value?: string | null): string | null {
  if (!value || !value.startsWith(SKILL_STYLE_PREFIX)) return null;
  const skillId = value.slice(SKILL_STYLE_PREFIX.length).trim();
  return skillId.length > 0 ? skillId : null;
}

export function isSkillStyleId(value?: string | null): boolean {
  return parseSkillStyleId(value) !== null;
}

/** Presets that shadow a catalog skill; the real skill replaces them in place. */
export const PRESET_SKILL_TWINS: Record<string, string> = {
  'preset-uae-national': 'uae-national-cv',
  'preset-fresh-graduate': 'uae-fresh-graduate-cv',
  'preset-relocate-gulf': 'relocate-to-gulf-cv',
};

export type CvPresetRow =
  | {
      kind: 'preset';
      /** The styleSegmentId this row applies (= preset id). */
      id: string;
      preset: CvPreset;
      layout: CvPresetLayoutConfig;
    }
  | {
      kind: 'skill';
      /** The styleSegmentId this row applies (= `skill:<skillId>`). */
      id: string;
      skill: SkillSummary;
      layout: CvPresetLayoutConfig;
    };

/**
 * The visual config a catalog skill implies, from its declared layout.
 * Falls back to the default look when the skill declares none, so applying it
 * never renders an undefined config.
 */
export function layoutForSkill(skill: SkillSummary): CvPresetLayoutConfig {
  const layout = skillLayoutSchema.safeParse(skill.layout);
  if (!layout.success) return DEFAULT_CV_PRESET_LAYOUT;
  return {
    style: skillTemplateToCvStyle(layout.data.template),
    color: skillAccentToCvColor(layout.data.accent),
    showPhoto: layout.data.photo,
  };
}

function skillRow(skill: SkillSummary): CvPresetRow {
  return {
    kind: 'skill',
    id: skillStyleId(skill.skillId),
    skill,
    layout: layoutForSkill(skill),
  };
}

export function buildCvPresetRows(
  skills: readonly SkillSummary[],
): CvPresetRow[] {
  const byId = new Map(skills.map((skill) => [skill.skillId, skill]));
  const used = new Set<string>();

  const rows: CvPresetRow[] = CV_PRESETS.map((preset) => {
    const twinId = PRESET_SKILL_TWINS[preset.id];
    const twin = twinId ? byId.get(twinId) : undefined;
    if (twin) {
      used.add(twin.skillId);
      return skillRow(twin);
    }
    return {
      kind: 'preset',
      id: preset.id,
      preset,
      layout: preset.layout,
    };
  });

  for (const skill of skills) {
    if (!used.has(skill.skillId)) {
      used.add(skill.skillId);
      rows.push(skillRow(skill));
    }
  }
  return rows;
}

export interface CvPresetRowEntry {
  row: CvPresetRow;
  label: string;
  description: string;
}

/** Search across the resolved label and description (same as filterCvPresets). */
export function filterCvPresetRows(
  entries: CvPresetRowEntry[],
  query: string,
): CvPresetRowEntry[] {
  const term = query.trim().toLowerCase();
  if (!term) return entries;
  return entries.filter(
    (entry) =>
      entry.label.toLowerCase().includes(term) ||
      entry.description.toLowerCase().includes(term),
  );
}
